import { Database } from "@/lib/agents/supabase"
import { DataTable, DataTableProps } from "./datatable"
import { supabase } from "@/lib/data"
import { useForceUpdate, useMultiState } from "@/hooks/use-multistate"
import { useEffect, useRef, useState } from "react"
import { cn, delay } from "@/lib/utils"
import { PaginationState } from "@tanstack/react-table"
import { ToolbarItem } from "./toolbaritem"
import { Clock } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip"
import { useLocation } from "react-router-dom"

declare global {

  type SupabaseFilterBuilder<T> = {
    eq: (column: T, value: any) => SupabaseFilterBuilder<any>
    neq: (column: T, value: any) => SupabaseFilterBuilder<any>
    gt: (column: T, value: any) => SupabaseFilterBuilder<any>
    gte: (column: T, value: any) => SupabaseFilterBuilder<any>
    lt: (column: T, value: any) => SupabaseFilterBuilder<any>
    lte: (column: T, value: any) => SupabaseFilterBuilder<any>
    like: (column: T, pattern: string) => SupabaseFilterBuilder<any>
    ilike: (column: T, pattern: string) => SupabaseFilterBuilder<any>
    is: (column: T, value: null | boolean) => SupabaseFilterBuilder<any>
    in: (column: T, values: any[]) => SupabaseFilterBuilder<any>
    contains: (column: T, value: any) => SupabaseFilterBuilder<any>
    containedBy: (column: T, value: any) => SupabaseFilterBuilder<any>
    rangeGt: (column: T, range: string) => SupabaseFilterBuilder<any>
    rangeGte: (column: T, range: string) => SupabaseFilterBuilder<any>
    rangeLt: (column: T, range: string) => SupabaseFilterBuilder<any>
    rangeLte: (column: T, range: string) => SupabaseFilterBuilder<any>
    rangeAdjacent: (column: T, range: string) => SupabaseFilterBuilder<any>
    overlaps: (column: T, value: any) => SupabaseFilterBuilder<any>
    textSearch: (column: T, query: string, options?: { type?: 'plain' | 'phrase' | 'websearch'; config?: string }) => SupabaseFilterBuilder<any>
    match: (query: T) => SupabaseFilterBuilder<any>
    not: (column: T, operator: string, value: any) => SupabaseFilterBuilder<any>
    or: (filters: string) => SupabaseFilterBuilder<any>
    filter: (column: T, operator: string, value: any) => SupabaseFilterBuilder<any>
  }

  type KeysOfTableOrView<T extends AllKeys> =
    T extends keyof Database["public"]["Tables"]
    ? keyof Database["public"]["Tables"][T]["Row"]
    : T extends keyof Database["public"]["Views"]
    ? keyof Database["public"]["Views"][T]["Row"]
    : never


}

type AllKeys = TableKeys | ViewKeys

interface Props<Q extends AllKeys> extends Omit<DataTableProps, "sort"> {
  table: Q
  select?: string
  filter?: (builder: SupabaseFilterBuilder<KeysOfTableOrView<Q>>) => SupabaseFilterBuilder<any>
  sort?: [KeysOfTableOrView<Q>, "asc" | "desc"]
  realtime?: boolean
  realtTimeThrottle?: number
  selectedRows?: string[]
  onSelectionChange?: (selectedIds: string[]) => void
  databaseFunction?: string
  databaseFunctionArgs?: any
  onExternalLoad?: () => Promise<{ data: any[], error?: any }>
  disabled?: boolean
  enableServerSideFiltering?: boolean
  searchableColumns?: (KeysOfTableOrView<Q> | string)[]
}

function applyFilters(query: any, search?: string, searchableColumns?: string[]) {
  if (search && search.trim() && searchableColumns && searchableColumns.length > 0) {
    const searchPattern = search.trim()
    if (searchableColumns.length === 1) {
      query = query.ilike(searchableColumns[0], `%${searchPattern}%`)
    } else {
      const orConditions = searchableColumns.map(col => `${col}.ilike.%${searchPattern}%`).join(',')
      query = query.or(orConditions)
    }
  }

  return query
}

async function readFromSupabase({ table, select = "*", filter, orderBy, page, pageSize, onExternalLoad, search, searchableColumns }: { table: string, select?: string, filter: any, orderBy: any, page: number, pageSize: number, onExternalLoad?: () => Promise<{ data: any[], error?: any }>, search?: string, searchableColumns?: string[] }) {

  if (onExternalLoad) {
    const res = await onExternalLoad()
    if (res) {
      const { data, error } = res
      return { data, count: data.length, error }
    }
  }

  let sbqCount = filter(supabase.from(table as any).select(select, { count: 'exact', head: true }))
  sbqCount = applyFilters(sbqCount, search, searchableColumns)

  let { data: nodata, error: countError, count } = await sbqCount

  let sbq = supabase.from(table as any).select(select)

  if (countError) {
    count = undefined
    console.log("Pre Count error, using the exact count in the query")
    sbq = supabase.from(table as any).select(select, { count: 'exact' })
  }

  if (orderBy) sbq = sbq.order(orderBy[0] as string, { ascending: orderBy[1] === 'asc' })
  sbq = filter(sbq as any) as any
  sbq = applyFilters(sbq, search, searchableColumns) as any

  const from = page * pageSize
  const to = from + pageSize - 1
  sbq = sbq.range(from, to)
  const { data, error, count: finalCount } = await sbq
  if (count == undefined) count = finalCount
  return { data, count, error }

}

function defaultFilter(q: any) { return q }

export function DataTableSupabase<Q extends AllKeys>(props: Props<Q>) {

  let {
    table,
    select,
    filter = defaultFilter,
    sort,
    realtime,
    realtTimeThrottle = 2000,
    onActionExecuted,
    selectedRows,
    onSelectionChange,
    disabled,
    enableServerSideFiltering = false,
    searchableColumns,
    controller,
    configKey,
    ...rest
  } = props

  const [realTime, setRealTime] = useState(false)
  const [{ pageIndex: page, pageSize }, setPage] = useMultiState<PaginationData>({ pageIndex: 0, pageSize: 20 })
  const [sortInfo, setSortInfo] = useState<[any, "asc" | "desc"] | null>(sort)
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})
  const [debouncedSearch, setDebouncedSearch] = useState<string>("")
  const update = useForceUpdate()
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const location = useLocation()

  if (!configKey) configKey = `${location.pathname}.${table}`

  const state = useRef({
    loading: true,
    count: 0,
    data: [],
    realTimeListening: false,
    readyForRealTime: false,
    error: "",
    version: 0, //used to force a reload
    lastNotifiedSelection: [] as string[],
  })

  async function loadData() {
    let data = []
    let error = null
    let count = 0
    let d = 1000
    let orderBy = sortInfo || null

    for (let n = 0; n < 10; n++) {
      const { data: rData, error: rError, count: rCount } = await readFromSupabase({
        table: String(table),
        select: select || "*",
        filter,
        orderBy,
        page,
        pageSize,
        onExternalLoad: props.onExternalLoad || (() => null),
        search: enableServerSideFiltering ? debouncedSearch : undefined,
        searchableColumns: enableServerSideFiltering && searchableColumns ? searchableColumns.map(c => String(c)) : undefined,
      })

      if (!rError) {
        data = rData
        count = rCount
        error = null
        break
      }
      await delay(d)
      console.log(`Retrying ${n + 1}...`)
      d += 1000
      error = rError
    }
    state.current.error = error
    return { data, count, error }
  }

  // Export needs the whole result set, not just the page in memory, so it pages
  // through the same query the grid is showing (filter, order and search included)
  async function exportLoadAll(maxRows: number) {
    const batchSize = Math.min(1000, maxRows)
    const rows: any[] = []

    for (let p = 0; rows.length < maxRows; p++) {
      const { data, error } = await readFromSupabase({
        table: String(table),
        select: select || "*",
        filter,
        orderBy: sortInfo || null,
        page: p,
        pageSize: batchSize,
        onExternalLoad: props.onExternalLoad || (() => null),
        search: enableServerSideFiltering ? debouncedSearch : undefined,
        searchableColumns: enableServerSideFiltering && searchableColumns ? searchableColumns.map(c => String(c)) : undefined,
      })

      if (error) throw error
      if (!data || data.length === 0) break
      rows.push(...data)
      if (props.onExternalLoad) break // external loads ignore paging and return everything at once
      if (data.length < batchSize) break
    }

    return rows.slice(0, maxRows)
  }

  useEffect(() => {
    if (selectedRows) {
      const newSelection: Record<string, boolean> = {}
      selectedRows.forEach(id => {
        newSelection[id] = true
      })
      setRowSelection(newSelection)
    }
  }, [selectedRows])

  useEffect(() => {
    if (onSelectionChange) {
      const selectedIds = Object.keys(rowSelection).filter(id => rowSelection[id])
      const lastSelection = state.current.lastNotifiedSelection
      const hasChanged =
        selectedIds.length !== lastSelection.length ||
        selectedIds.some(id => !lastSelection.includes(id)) ||
        lastSelection.some(id => !selectedIds.includes(id))

      if (hasChanged) {
        state.current.lastNotifiedSelection = selectedIds
        onSelectionChange(selectedIds)
      }
    }
  }, [rowSelection, onSelectionChange])

  useEffect(() => {
    // console.log("Reloading...")
    if (disabled) return

    state.current.loading = true
    update()
    loadData().then(d => {
      // console.log("Reload")
      if (d.error) return
      state.current.data = d.data
      state.current.count = d.count
      state.current.loading = false
      state.current.readyForRealTime = true
      update()
      // console.log("Reloaded")
    })
  }, [table, select, sortInfo, page, pageSize, state.current.version, enableServerSideFiltering ? debouncedSearch : undefined])
  // }, [table, select, filter, sortInfo, page, pageSize, state.current.version, enableServerSideFiltering ? debouncedSearch : undefined])

  useEffect(() => {
    if (!realtime) return
    if (!state.current.readyForRealTime) return
    if (state.current.error) return
    if (!realTime) return
    console.log("Realtime listening")
    const s = supabase.channel('realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: String(table) }, async payload => {
        console.log('Change received!', payload)
        if (payload.eventType == "INSERT" && state.current.data) {
          state.current.data = [...state.current.data, payload.new]
          state.current.count++
          update()
        } else if (payload.eventType == "DELETE") {
          state.current.data = state.current.data.filter(d => d.id !== payload.old.id)
        } else if (payload.eventType == "UPDATE") {
          state.current.data = state.current.data.map(d => d.id == payload.new.id ? payload.new : d)
        }
      })
      .subscribe()
    return () => { s.unsubscribe() }
  }, [table, state.current.readyForRealTime, state.current.error, realTime])

  function onPaginationChange(s: PaginationState) {
    state.current.loading = true
    update()
    setPage(s)
  }

  function onSortingChange(field: string, direction: "asc" | "desc" | undefined) {
    setSortInfo((direction ? [field, direction] : null) as any)
  }

  if (controller) {
    controller.reload = async () => {
      console.log("Reloading...")
      setRowSelection({})
      state.current.version++
      update()
    }

    controller.setLoading = (loading: boolean) => {
      if (loading !== state.current.loading) {
        state.current.loading = loading
        update()
      }
    }

  }

  async function aexec() {
    console.log("Action on Supabase Datatable Executed")
    setRowSelection({})
    state.current.version++
    update()
  }

  function handleGlobalFilterChange(search: string) {
    if (enableServerSideFiltering) {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }

      debounceTimeoutRef.current = setTimeout(() => {
        setDebouncedSearch(search)
        setPage({ pageIndex: 0, pageSize })
      }, 300)
    }
  }

  return <DataTable
    {...rest as any}
    controller={controller}
    loading={state.current.loading}
    data={state.current.data}
    sort={sortInfo}
    configKey={configKey}
    onPaginationChange={onPaginationChange}
    onSortingChange={onSortingChange}
    total={state.current.count}
    onActionExecuted={onActionExecuted || aexec}
    rowSelection={rowSelection}
    onRowSelectionChange={setRowSelection}
    onGlobalFilterChange={enableServerSideFiltering ? handleGlobalFilterChange : undefined}
    enableServerSideFiltering={enableServerSideFiltering}
    exportFileName={props.exportFileName || String(table)}
    onExportLoadAll={props.onExportLoadAll || exportLoadAll}
  >
    {props.children}
    {realtime && <ToolbarItem>
      <Tooltip>
        <TooltipTrigger>
          <Clock size={16} strokeWidth={4} onClick={() => setRealTime(!realTime)} className={cn("cursor-pointer stroke-2", { "text-red-500": realTime, "animate-pulse": realTime })} />
        </TooltipTrigger>
        <TooltipContent>
          <p>Realtime: {realTime ? "On" : "Off"}</p>
        </TooltipContent>
      </Tooltip>
    </ToolbarItem>}

  </DataTable>


}
